/**
 * Brand-specific configuration
 * 
 * Assets in /public/brand/ are swapped at build time from brand repository.
 * Colors and texts can be configured via environment variables if needed.
 */

export const BRAND_CONFIG = {
  // Brand identity
  name: import.meta.env.VITE_BRAND_NAME || 'Dina',
  
  // Currency settings
  currency: {
    // Symbol for display (₸ for Kazakhstan, ₽ for Russia)
    symbol: import.meta.env.VITE_BRAND_CURRENCY_SYMBOL || '₸',
    // ISO 4217 code for Intl.NumberFormat
    code: import.meta.env.VITE_BRAND_CURRENCY_CODE || 'KZT',
    // Locale for number formatting
    locale: import.meta.env.VITE_BRAND_LOCALE || 'ru-KZ',
  },
  
  // Colors (can be overridden via env, but defaults are for Dina)
  colors: {
    primary: import.meta.env.VITE_BRAND_COLOR_PRIMARY || '#67b437',
    primaryHover: import.meta.env.VITE_BRAND_COLOR_PRIMARY_HOVER || '#5a9e30',
    primaryActive: import.meta.env.VITE_BRAND_COLOR_PRIMARY_ACTIVE || '#4d8829',
    accent: import.meta.env.VITE_BRAND_COLOR_ACCENT || '#ef6e1a',
    secondary: import.meta.env.VITE_BRAND_COLOR_SECONDARY || '#58c1d4',
  },
  
  // Assets from /public/brand/ (swapped at build time)
  images: {
    logo: '/brand/logo.svg',
    logoIcon: '/brand/logo-icon.svg',
    favicon: '/brand/logo-icon.svg',
    productPlaceholder: '/brand/product-placeholder.svg',
    loyaltyIcon: '/brand/loyalty-icon.svg',
    mapPin: '/brand/map-pin.svg',
    mapPinActive: '/brand/map-pin-active.svg',
  },
  
  // Loyalty program (can move to locales if needed)
  loyalty: {
    name: {
      ru: 'Программа лояльности',
      kk: 'Адалдық бағдарламам',
      en: 'Loyalty Program',
    },
    nav: {
      ru: 'Моя программа лояльности',
      kk: 'Менің адалдық бағдарламам',
      en: 'My Loyalty Program',
    },
  },
}

/**
 * Applies brand colors as CSS custom properties to :root
 * Call this in the app entry point (main.tsx)
 */
export function applyBrandColors (): void {
  const root = document.documentElement
  const { colors } = BRAND_CONFIG
  
  // Primary color and its variations
  root.style.setProperty('--color-primary', colors.primary)
  root.style.setProperty('--color-primary-hover', colors.primaryHover)
  root.style.setProperty('--color-primary-active', colors.primaryActive)
  root.style.setProperty('--accent-green-main', colors.primary) // Legacy support
  
  // Alpha versions of primary
  root.style.setProperty('--accent-green-200', hexToRgba(colors.primary, 0.2))
  root.style.setProperty('--accent-green-100', hexToRgba(colors.primary, 0.1))
  root.style.setProperty('--color-link-bg-alpha', hexToRgba(colors.primary, 0.2))
  
  // Secondary/accent colors
  root.style.setProperty('--accent-orange', colors.accent)
  root.style.setProperty('--color-highlight', colors.accent)
  root.style.setProperty('--accent-blue', colors.secondary)
  root.style.setProperty('--color-secondary', colors.secondary)
}

/**
 * Convert hex color to rgba
 */
function hexToRgba (hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
